<template>
  <div class="auction-table">
    <Toast ref="toast" />

    <DataTable :value="tableData" dataKey="id" responsiveLayout="scroll">
      <!--      <Column expander style="width: 3rem" />-->

      <!-- ================= АРТЕФАКТ ================= -->
      <Column field="name" header="Артефакт">
        <template #body="{ data }">
          <div
            :class="{
              'row-highlight': highlightMap[data.id]
            }"
            style="white-space: nowrap; position: relative"
          >
            <span @click="copyToClipboard(data.name, 'name')" v-text="data.name"></span>&nbsp;
            <span style="white-space: nowrap" :style="`color: ${getColorQlt(data.qlt)}`">
              {{ data.additionalInfo?.ptn ?? data.ptn }}
            </span>

            <span v-if="highlightMap[data.id]" class="click-indicator">
              👤 {{ highlightMap[data.id].adminId }}
            </span>
          </div>
        </template>
      </Column>

      <Column field="averagePrice" header="Медиана">
        <template #body="{ data }">
          {{ formatPrice(Number(data.averagePrice)) }}
        </template>
      </Column>

      <Column field="salePrice" header="Цена продажи">
        <template #body="{ data }">
          <div
            :class="{
              'row-highlight': highlightMapPrice[data.id]
            }"
            style="white-space: nowrap; position: relative"
          >
            <span @click="copyToClipboard(data.salePrice, 'price')">{{
              formatPrice(Number(data.salePrice))
            }}</span>

            <span v-if="highlightMapPrice[data.id]" class="click-indicator">
              👤 {{ highlightMapPrice[data.id].adminId }}
            </span>
          </div>
        </template>
      </Column>

      <Column field="currentElems" header="Лоты">
        <template #body="{ data }">
          <div>
            <span v-for="(lot, index) in data.currentElems" :key="index">
              <span>{{ lot.price }}</span
              >;
            </span>
          </div>
        </template>
      </Column>

      <Column field="currentElems" header="Время осталось">
        <template #body="{ data }">
          <div v-if="data.currentElems.length">
            <div style="white-space: nowrap">
              {{ formatMs(getAuctionTimer(data.startTime, data.endTime).leftMs) }}
            </div>
          </div>
        </template>
      </Column>

      <Column header="Ликвидность 48ч">
        <template #body="{ data }">
          {{ calcLiquidity48h(data.historySalePrices) }}
        </template>
      </Column>

      <Column field="historySalePrices" header="Количество проданных">
        <template #body="{ data }">
          <div>
            {{ data.historySalePrices.length }}
          </div>
          <div>
            <HistoryChart
              :historySalePrices="data.historySalePrices"
              :sale-price="data.salePrice"
            />
          </div>
        </template>
      </Column>

      <Column field="startPrice" header="Текущая ставка">
        <template #body="{ data }">
          <span>{{ formatPrice(Number(data.startPrice)) }}</span>
        </template>
      </Column>

      <Column field="startPrice" header="Ставка на перебив">
        <template #body="{ data }">
          <span @click="copyToClipboard(calculatedBid(data))">{{
            formatPrice(Number(calculatedBid(data)))
          }}</span>
        </template>
      </Column>

      <Column field="profit" header="Возможный профит">
        <template #body="{ data }">
          {{ formatPrice(Number(data.profit)) }}
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  defineProps,
  withDefaults,
  defineExpose
} from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

import { formatPrice } from '@/common/function'
import { getNameArtFromId } from '@/common/list'
import { useWebSocketStore } from '@/store/websocket.store'
import { copyText } from 'vue3-clipboard'
import Toast from 'primevue/toast'
import HistoryChart from '@/components/HistoryChart.vue'

const props = withDefaults(
  defineProps<{
    enteredProfit?: number
  }>(),
  {
    enteredProfit: 0
  }
)

const toast = ref()

const wsStore = useWebSocketStore()

const tableData = ref<any[]>([])

// const expandedRows = ref<Record<string, boolean>>({});

const qltMap: Record<number, string> = {
  0: '#E0E0E0',
  1: '#4CAF50',
  2: '#2196F3',
  3: '#9C27B0',
  4: '#F44336',
  5: '#FFD700'
}

const getColorQlt = (qlt: number | undefined) => qltMap[qlt ?? 0] ?? 'white'

interface CurrentLot {
  price: number
  startTime: string
  endTime: string
  currentPrice: number
  additionalInfo: object | null
}

export interface TableRow {
  qlt: number
  ptn: string
  averagePrice: string | null
  historyMinPrice: number
  historySalePrices: HistoryLot[]
  currentPrice: number
  currentElems: CurrentLot[]
  profit: string | null
  commission: number | null
  salePrice: number | null
  startPrice: number | null
  startTime: string | null
  endTime: string | null
  isBid?: boolean
  additionalInfo: object | null
}

interface HistoryLot {
  price: number
  time: string | Date
  additional: object | null
}

function onArtefactProfitAll(data: any) {
  const next: any[] = []

  for (const artefactId in data.artefactProfitMap) {
    const artefact = data.artefactProfitMap[artefactId]
    if (!artefact?.rows) continue

    const name = getNameArtFromId(artefactId)

    artefact.rows.forEach((row: any, index: number) => {
      const item = {
        ...row,
        name,
        id: `${artefactId}_${index}`
      }

      next.push(item)
    })
  }

  next.sort((a, b) => {
    return new Date(a.endTime).getTime() - new Date(b.endTime).getTime()
  })

  tableData.value = next.filter(i => {
    if (i.isBid) {
      const bidWithProfit = calculatedBid(i)
      if (bidWithProfit > i.currentBid) {
        return true
      }
    }
    return false
  })
}

const copyingNotification = (text: string) => {
  toast.value?.add({
    severity: 'info',
    summary: 'Скопировано',
    detail: text,
    life: 3000
  })
}

const copyToClipboard = (text: string, type: string) => {
  copyingNotification(text)
  copyText(text, undefined, (error: Error) => {
    if (error) console.error('Copy error:', error)
  })

  wsStore.sendArt({
    text,
    type
  })
}

function getAuctionTimer(startIso: string, endIso: string) {
  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  const now = Date.now()

  // const duration = end - start;
  const passed = Math.max(0, now - start)
  const left = Math.max(0, end - now)

  return {
    passedMs: passed,
    leftMs: left
  }
}

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000)
  const m = Math.floor(s / 60)
  const h = Math.floor(m / 60)

  if (h > 0) return `${h}ч ${m % 60}м`
  if (m > 0) return `${m}м ${s % 60}с`
  return `${s}с`
}

function calculatedBid(data) {
  const firstElemPrice = data.currentElems[0]?.price

  let salePrice = firstElemPrice <= data.averagePrice ? firstElemPrice - 1 : data.averagePrice

  const commission = salePrice * 0.05
  const result = salePrice - props.enteredProfit - commission

  return Math.floor(result)
}

function calcLiquidity48h(history: HistoryLot[]): number {
  if (!history?.length) return 0

  const now = Date.now()
  const WINDOW = 48 * 60 * 60 * 1000

  const times = history.map(h => new Date(h.time).getTime()).sort((a, b) => a - b)

  // ===== 1. продажи за 48ч =====
  const last48h = times.filter(t => t >= now - WINDOW).length

  // 6 продаж за 48ч = потолок
  const salesScore = Math.min(last48h / 6, 1) * 60

  // ===== 2. средний gap =====
  let avgGap = WINDOW

  if (times.length > 1) {
    let sum = 0
    for (let i = 1; i < times.length; i++) {
      sum += times[i] - times[i - 1]
    }
    avgGap = sum / (times.length - 1)
  }

  // 6ч → отлично, 48ч → 0
  const gapScore = Math.max(0, 1 - avgGap / WINDOW) * 25

  // ===== 3. свежесть =====
  const lastSaleAgo = now - times[times.length - 1]

  // <6ч отлично, >48ч мёртв
  const recencyScore = Math.max(0, 1 - lastSaleAgo / WINDOW) * 15

  return Math.round(Math.min(100, salesScore + gapScore + recencyScore))
}

const highlightMap = ref<Record<string, { adminId: number; until: number }>>({})

const highlightMapPrice = ref<Record<string, { adminId: number; until: number }>>({})

function onAdminClick(data) {
  if (data.type === 'name') {
    const item = tableData.value.find(i => i.name === data.text)
    if (!item) return

    const duration = 2000 // 2 секунды

    highlightMap.value[item.id] = {
      adminId: data.adminId,
      until: Date.now() + duration
    }

    setTimeout(() => {
      delete highlightMap.value[item.id]
    }, duration)
  } else if (data.type === 'price') {
    const item = tableData.value.find(i => i.salePrice === data.text)
    if (!item) return

    const duration = 2000 // 2 секунды

    highlightMapPrice.value[item.id] = {
      adminId: data.adminId,
      until: Date.now() + duration
    }

    setTimeout(() => {
      delete highlightMapPrice.value[item.id]
    }, duration)
  }
}

watch(
  [() => wsStore.artefactProfitData, () => props.enteredProfit],
  ([data]) => {
    onArtefactProfitAll(data)
  },
  { immediate: true }
)

onMounted(() => {
  // wsStore.connect() // 👈 ВАЖНО

  wsStore.artefactSocket?.on('admin_click', onAdminClick)
})

onBeforeUnmount(() => {
  wsStore.artefactSocket?.off('admin_click', onArtefactProfitAll)
  tableData.value = []
})
</script>

<style lang="scss" scoped>
body {
  background-color: #1e1e1e;
  color: #f0e6d2;
  font-family: 'Arial', sans-serif;
  margin: 0;
  padding: 0;
}

.auction-table {
  width: 95%;
  margin: 20px auto;
  background-color: #2c2c2c;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
  padding: 20px;
}

.artifact-info {
  font-size: 12px;
  line-height: 1.4;
}

.row {
  display: flex;
  gap: 8px;
}

.key {
  min-width: 140px;
  color: #9aa0a6;
}

.value {
  word-break: break-word;
}

.row-highlight {
  animation: flash 0.6s ease-in-out 3;
  background-color: rgba(255, 255, 0, 0.25);
}

@keyframes flash {
  0% {
    background-color: rgba(255, 255, 0, 0.1);
  }
  50% {
    background-color: rgba(255, 200, 0, 0.5);
  }
  100% {
    background-color: rgba(255, 255, 0, 0.1);
  }
}

.click-indicator {
  position: absolute;
  left: 0;
  top: 20px;
  font-size: 11px;
  color: #ff9800;
}
</style>
